import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EventProvider } from './contexts/EventContext';
import { OrgProvider } from './contexts/OrgContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import EmailVerificationScreen from './components/EmailVerificationScreen';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import ParticipantDashboard from './pages/ParticipantDashboard';
import Events from './pages/Events';
import Teams from './pages/Teams';
import EventOverview from './pages/EventOverview';
import EventSettings from './pages/EventSettings';
import Categories from './pages/Categories';
import CategoryGroups from './pages/CategoryGroups';
import WODs from './pages/WODs';
import Heats from './pages/Heats';
import EventStaff from './pages/EventStaff';
import JudgePlan from './pages/JudgePlan';
import DayOf from './pages/DayOf';
import CourseStations from './pages/CourseStations';
import CourseParts from './pages/CourseParts';
import DisplaySettings from './pages/DisplaySettings';
import Archives from './pages/Archives';
import Settings from './pages/Settings';
import Account from './pages/Account';
import Notifications from './pages/Notifications';
import EventRegistrationSettings from './pages/EventRegistrationSettings';
import EventRegistrations from './pages/EventRegistrations';
import EventCrossFitResults from './pages/EventCrossFitResults';
import EventRaceResults from './pages/EventRaceResults';
import Registrations from './pages/Registrations';
import Volunteers from './pages/Volunteers';
import StripeOnboarding from './pages/StripeOnboarding';
import AdminLicenses from './pages/AdminLicenses';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminAppPromos from './pages/AdminAppPromos';
import AdminKartCodes from './pages/AdminKartCodes';
import AdminAppFeatureFlags from './pages/AdminAppFeatureFlags';
import AdminTransactions from './pages/AdminTransactions';
import AdminRevenue from './pages/AdminRevenue';
import AdminVatRegimes from './pages/AdminVatRegimes';
import Billing from './pages/Billing';
import MyLicense from './pages/MyLicense';
import Downloads from './pages/Downloads';
import Messages from './pages/Messages';
import { PUBLIC_SITE_URL } from './config/api';

// Composant pour rediriger vers le site public
const RedirectToPublicSite: React.FC<{ path?: string }> = ({ path = '' }) => {
  React.useEffect(() => {
    window.location.href = `${PUBLIC_SITE_URL}${path}`;
  }, [path]);
  return <div>Redirection vers {PUBLIC_SITE_URL}...</div>;
};

// Ancienne URL /scores → page Résultats unifiée, ouverte directement en mode édition.
const ScoresEditRedirect: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/admin/events/${eventId}/crossfit-results?edit=1`} replace />;
};

// Client React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Composant pour protéger les routes - redirige vers logfit.eu si non connecté
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  if (!isAuthenticated) {
    // Rediriger vers logfit.eu pour le login
    window.location.href = `${PUBLIC_SITE_URL}/login?redirect=${encodeURIComponent(window.location.href)}`;
    return <div>Redirection vers la page de connexion...</div>;
  }

  return <>{children}</>;
};

// Composant pour protéger les routes admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  if (!isAuthenticated) {
    window.location.href = `${PUBLIC_SITE_URL}/login?redirect=${encodeURIComponent(window.location.href)}`;
    return <div>Redirection vers la page de connexion...</div>;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

// Composant pour protéger les routes admin/organizer (pas participants)
const OrganizerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  if (!isAuthenticated) {
    window.location.href = `${PUBLIC_SITE_URL}/login?redirect=${encodeURIComponent(window.location.href)}`;
    return <div>Redirection vers la page de connexion...</div>;
  }

  // Les participants ne peuvent pas accéder au dashboard organisateur
  if (user?.role === 'participant') {
    window.location.href = PUBLIC_SITE_URL;
    return <div>Redirection...</div>;
  }

  // Organisateurs non vérifiés voient l'écran de vérification (admin bypass)
  if (user?.role !== 'admin' && user?.email_verified === false) {
    return <EmailVerificationScreen />;
  }

  return <>{children}</>;
};

// Composant de redirection intelligente selon le rôle
const SmartDashboardRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  // Si admin : redirect vers dashboard admin avec gestion users
  if (user?.role === 'admin') {
    return <Navigate to="/admin/users" replace />;
  }

  // Si participant : redirect vers logfit.eu (pas accès au dashboard organisateur)
  if (user?.role === 'participant') {
    window.location.href = PUBLIC_SITE_URL;
    return <div>Redirection...</div>;
  }

  // Si organisateur : atterrissage sur Événements (le Tableau de bord n'est plus dans le menu)
  return <Navigate to="/admin/events" replace />;
};

// Composant de redirection pour la racine
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  // Non connecté → redirection vers logfit.eu
  window.location.href = PUBLIC_SITE_URL;
  return <div>Redirection vers {PUBLIC_SITE_URL}...</div>;
};

// Composant pour les pages à venir
const ComingSoon: React.FC<{ title: string }> = ({ title }) => {
  return (
    <div className="p-5 text-foreground text-center mt-24">
      <h2 className="text-2xl font-bold mb-3">{title}</h2>
      <p className="text-muted-foreground">Cette page est en cours de développement...</p>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* UN SEUL provider de tooltips : délais cohérents partout, et le
            « skip » (2ᵉ tooltip immédiat) ne marche qu'au sein d'un même provider. */}
        <TooltipProvider delayDuration={200} skipDelayDuration={500}>
        <Router>
          <AuthProvider>
            <Routes>
              {/* Redirection login/signup vers logfit.eu */}
              <Route path="/login" element={<RedirectToPublicSite path="/login" />} />
              <Route path="/signup" element={<RedirectToPublicSite path="/signup" />} />

              {/* Routes de redirection */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Navigate to="/admin" replace />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Navigate to="/admin/settings" replace />
                </ProtectedRoute>
              } />

              {/* Routes publiques → redirection vers logfit.eu */}
              <Route path="/events" element={<RedirectToPublicSite path="/events" />} />
              <Route path="/events/:slug" element={<RedirectToPublicSite path="/events" />} />
              <Route path="/events/:slug/register" element={<RedirectToPublicSite path="/events" />} />
              <Route path="/registration/success" element={<RedirectToPublicSite path="/registration/success" />} />

              {/* Route upgrade organisateur → redirection vers logfit.eu */}
              <Route path="/become-organizer" element={<RedirectToPublicSite path="/become-organizer" />} />

              {/* Routes publiques organisation → redirection vers logfit.eu */}
              <Route path="/:orgSlug" element={<RedirectToPublicSite />} />
              <Route path="/:orgSlug/events/:eventSlug/results" element={<RedirectToPublicSite />} />
              <Route path="/:orgSlug/events/:eventSlug" element={<RedirectToPublicSite />} />
              <Route path="/:orgSlug/event" element={<RedirectToPublicSite />} />
              <Route path="/:orgSlug/events" element={<RedirectToPublicSite />} />

              {/* Routes administrateur protégées */}
              <Route path="/admin" element={
                <OrganizerRoute>
                  {/* OrgProvider AU-DESSUS d'EventProvider : l'organisation de
                      travail d'un admin filtre la liste des événements. */}
                  <OrgProvider>
                    <EventProvider>
                      {/* Sous EventProvider : la room temps réel est celle de
                          l'organisation de l'événement sélectionné (LOG-201). */}
                      <RealtimeProvider>
                        <Layout />
                      </RealtimeProvider>
                    </EventProvider>
                  </OrgProvider>
                </OrganizerRoute>
              }>
                <Route index element={<SmartDashboardRedirect />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="my-participations" element={<ParticipantDashboard />} />
                <Route path="users" element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                } />
                <Route path="licenses" element={
                  <AdminRoute>
                    <AdminLicenses />
                  </AdminRoute>
                } />
                <Route path="promo-codes" element={
                  <AdminRoute>
                    <AdminPromoCodes />
                  </AdminRoute>
                } />
                <Route path="app-promos" element={
                  <AdminRoute>
                    <AdminAppPromos />
                  </AdminRoute>
                } />
                <Route path="kart-codes" element={
                  <AdminRoute>
                    <AdminKartCodes />
                  </AdminRoute>
                } />
                <Route path="app-feature-flags" element={
                  <AdminRoute>
                    <AdminAppFeatureFlags />
                  </AdminRoute>
                } />
                <Route path="transactions" element={
                  <AdminRoute>
                    <AdminTransactions />
                  </AdminRoute>
                } />
                <Route path="revenue" element={
                  <AdminRoute>
                    <AdminRevenue />
                  </AdminRoute>
                } />
                <Route path="vat-regimes" element={
                  <AdminRoute>
                    <AdminVatRegimes />
                  </AdminRoute>
                } />
                <Route path="billing" element={
                  <AdminRoute>
                    <Billing />
                  </AdminRoute>
                } />
                <Route path="statements" element={<Billing />} />
                <Route path="events" element={<Events />} />
                <Route path="events/new" element={<Navigate to="/admin/events?create=true" replace />} />
                <Route path="events/:eventId/settings" element={<EventSettings />} />
                <Route path="events/:eventId/registration" element={<EventRegistrationSettings />} />
                <Route path="events/:eventId/registrations" element={<EventRegistrations />} />
                <Route path="events/:eventId/crossfit-results" element={<EventCrossFitResults />} />
                <Route path="events/:eventId/race-results" element={<EventRaceResults />} />
                <Route path="events/:eventId/scores" element={<ScoresEditRedirect />} />
                <Route path="events/:id" element={<Navigate to="/admin/events" replace />} />
                <Route path="my-license" element={<MyLicense />} />
                <Route path="downloads" element={<Downloads />} />
                {/* Ex-page Encaissements, fondue dans Facturation : les anciens liens y mènent. */}
                <Route path="earnings" element={<Navigate to="/admin/statements" replace />} />
                <Route path="registrations" element={<Registrations />} />
                <Route path="volunteers" element={<Volunteers />} />
                <Route path="messages" element={<Messages />} />
                <Route path="overview" element={<EventOverview />} />
                <Route path="teams" element={<Teams />} />
                <Route path="categories" element={<Categories />} />
                <Route path="category-groups" element={<CategoryGroups />} />
                <Route path="wods" element={<WODs />} />
                <Route path="wods/new" element={<WODs />} />
                <Route path="wods/:wodId/edit" element={<WODs />} />
                <Route path="wods/:wodId/parcours" element={<CourseParts />} />
                <Route path="heats" element={<Heats />} />
                {/* L'assignation a rejoint le planning (étape 2 de la refonte) :
                    on redirige plutôt que de supprimer, les liens et favoris
                    existants doivent continuer de fonctionner. */}
                <Route path="heats/assignment" element={<Navigate to="/admin/heats" replace />} />
                <Route path="staff" element={<EventStaff />} />
                <Route path="judge-plan" element={<JudgePlan />} />
                <Route path="day-of" element={<DayOf />} />
                <Route path="course-stations" element={<CourseStations />} />
                <Route path="display" element={<DisplaySettings />} />
                <Route path="archives" element={<Archives />} />
                <Route path="settings" element={<Settings />} />
                {/* « Mon compte » : la PERSONNE, pas une organisation. */}
                <Route path="account" element={<Account />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="settings/stripe" element={<StripeOnboarding />} />
                <Route path="settings/stripe/success" element={<StripeOnboarding />} />
                <Route path="settings/stripe/refresh" element={<StripeOnboarding />} />
                <Route path="profile" element={<ComingSoon title="Mon profil" />} />
              </Route>

              {/* Page d'accueil → redirection vers /admin si connecté, sinon logfit.eu */}
              <Route path="/" element={<RootRedirect />} />

              {/* Fallback → redirection vers logfit.eu */}
              <Route path="*" element={<RedirectToPublicSite />} />
            </Routes>
            <Toaster
              position="bottom-right"
              richColors
              closeButton
              theme="dark"
            />
          </AuthProvider>
        </Router>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
